import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';

const COLORS = {
  card: '#1a2332', cardBorder: '#2a3444', muted: '#8b95a5',
  orange: '#FF6B4A', green: '#00D084', cyan: '#00B8D4', red: '#FF4D4F', blue: '#1890FF',
};

export function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

const KPICard: React.FC<{ title: string; value: string | number; subtext?: string; valueColor: string }> = ({ title, value, subtext, valueColor }) => (
  <Card sx={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 0.5, height: '100%' }}>
    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Typography sx={{ fontSize: '0.68rem', color: COLORS.muted, fontWeight: 500, letterSpacing: 0.3, textTransform: 'uppercase', mb: 0.75 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, color: valueColor, lineHeight: 1.1 }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Typography>
      {subtext && <Typography sx={{ fontSize: '0.65rem', color: COLORS.muted, mt: 0.5 }}>{subtext}</Typography>}
    </CardContent>
  </Card>
);

interface ScopeData {
  total_count: number; unregistered_count: number; registered_count: number;
  registration_percentage: number; registered_today: number;
  /** Drive figures: unregistered at drive start, registered via the app since, and their ratio. */
  pool_count?: number; converted_count?: number; conversion_percentage?: number;
}

const DAILY_TARGET = 330;

interface Props {
  /** From useDashboardScopeSummary(scope) — reflects the current filter selection. */
  scopeData?: ScopeData;
  /** From useDashboardSummary() — national totals, used for officer counts (not scoped by location). */
  totalOfficers?: number;
  belowTargetCount: number;
}

/**
 * The 6-tile KPI row shared by Dashboard and Reports — reflects whatever
 * county/district/division/location scope the filter bar currently has
 * selected, not the national totals.
 */
export const DashboardKPIRow: React.FC<Props> = ({ scopeData, totalOfficers = 0, belowTargetCount }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: 1.5,
      mb: 2,
    }}
  >
    <KPICard
      title="Registered"
      value={scopeData ? formatCount(scopeData.registered_count) : '0'}
      subtext="on the IEBC voter roll"
      valueColor={COLORS.blue}
    />
    <KPICard
      title="Unregistered"
      value={scopeData ? formatCount(scopeData.unregistered_count) : '0'}
      subtext="not on the IEBC voter roll"
      valueColor={COLORS.orange}
    />
    <KPICard title="Registered Today" value={scopeData?.registered_today || 0} subtext={`target ${DAILY_TARGET}`} valueColor={COLORS.green} />
    <KPICard
      title="Converted to Date"
      value={`${(scopeData?.conversion_percentage || 0).toFixed(1)}%`}
      subtext={`${(scopeData?.converted_count || 0).toLocaleString()} registered via the app`}
      valueColor={COLORS.cyan}
    />
    <KPICard
      title="Target Achieved"
      value={`${Math.min(100, ((scopeData?.registered_today || 0) / DAILY_TARGET) * 100).toFixed(0)}%`}
      subtext={`of ${DAILY_TARGET} today`}
      valueColor={COLORS.red}
    />
    <KPICard title="Officers Reporting" value={totalOfficers} subtext={`of ${Math.max(9, totalOfficers)} chiefs · today`} valueColor={COLORS.blue} />
    <KPICard title="Below Target" value={belowTargetCount} subtext="0 declared pending · 0 verified" valueColor={COLORS.red} />
  </Box>
);
